import { createSlice } from '@reduxjs/toolkit';
import { filedGroupItemDataType, IFieldGroup, ILanguage, IPage, ISection } from 'constants/content.constants';
import { initialMessage, sideBarSystemManagementItem, userMessage } from 'constants/ui.constants';

interface IContentState {
	category: string | null;
	feature: string | null;
	permission: any;
	pages: IPage[];
	submenu: any;
	error: userMessage | null;
	loading: boolean;
	languages: ILanguage[];
	selectedLanguage: ILanguage | null;
	currentData: any[];
	originCurrentData: any[];
	currentTab: number;
	uploadingFile: boolean;
}

const initialState: IContentState = {
	category: null,
	feature: null,
	permission: null,
	pages: [],
	submenu: [],
	error: initialMessage,
	loading: false,
	languages: [],
	selectedLanguage: null,
	currentData: [],
	currentTab: 0,
	uploadingFile: false,
	originCurrentData: [],
};

export const contentSlice = createSlice({
	name: 'content',
	initialState,
	reducers: {
		setCategory: (state, { payload }) => {
			state.category = payload;
		},
		setFeature: (state, { payload }) => {
			state.feature = payload;
		},
		setPermission: (state, { payload }) => {
			state.permission = payload[0];
		},
		setSubMenu: (state, { payload }) => {
			if (payload.nestedSubmenu) {
				const firstElement = sideBarSystemManagementItem.children[0];
				const restElements = sideBarSystemManagementItem.children.slice(
					1,
					sideBarSystemManagementItem.children.length
				);
				state.submenu = [firstElement, ...payload.items, ...restElements];
			} else {
				state.submenu = payload.items;
			}
		},
		setPages: (state, { payload }) => {
			state.pages = payload;
		},
		setLanguages: (state, { payload }) => {
			state.languages = payload;
			state.selectedLanguage = state.languages[0];
		},
		setSelectedLanguage: (state, { payload }) => {
			state.selectedLanguage = state.languages.filter((language: any) => language.displayName === payload)[0];
		},
		setCurrentData: (state) => {
			state.currentData = [];
			state.pages?.map((page: IPage) => {
				page.sections?.map((section: ISection) => {
					section.fieldGroups?.map((fieldGroup: IFieldGroup) => {
						fieldGroup.fieldGroupItems?.map((fieldGroupItem: any) => {
							state.currentData = [...state.currentData, fieldGroupItem];
						});
					});
				});
			});
		},
		setOriginCurrentData: (state) => {
			state.originCurrentData = [...state.currentData];
		},
		updateCurrentData: (state, { payload }) => {
			const { dataType, data, itemId } = payload;
			state.currentData?.map((element: any) => {
				if (element.id === itemId) {
					switch (dataType) {
						case filedGroupItemDataType.IMAGE:
							element.imageId = data.image.id;
							element.image = data.image;
							break;
						default:
							element[data.languageCode] = data.text;
							break;
					}
				}
			});
		},
		setCurrentTab: (state, { payload }) => {
			state.currentTab = payload;
		},
		setUploadingFile: (state, { payload }) => {
			state.uploadingFile = payload;
		},
		setError: (state, { payload }) => {
			state.error = payload;
		},
		setLoading: (state, { payload }) => {
			state.loading = payload;
		},
	},
});

export const getError = (state: any) => {
	return state.content.error;
};

export const isLoading = (state: any) => state.content.loading;

export const getSubMenu = (state: any) => state.content.submenu;

export const getCategory = (state: any) => state.content.category;

export const getFeature = (state: any) => state.content.feature;

export const getPermission = (state: any) => state.content.permission;

export const getPages = (state: any) => state.content.pages;

export const getLanguages = (state: any) => state.content.languages;

export const getSelectedLanguage = (state: any) => state.content.selectedLanguage;

export const getCurrentTab = (state: any) => state.content.currentTab;

export const getUploadingFile = (state: any) => state.content.uploadingFile;

export const getCurrentData = (state: any) => state.content.currentData;

export const getOriginCurrentData = (state: any) => state.content.originCurrentData;

export const {
	setSubMenu,
	setError,
	setLoading,
	setCategory,
	setFeature,
	setPermission,
	setPages,
	setLanguages,
	setSelectedLanguage,
	setCurrentData,
	setCurrentTab,
	setUploadingFile,
	updateCurrentData,
	setOriginCurrentData,
} = contentSlice.actions;

export default contentSlice.reducer;
